import React, { useEffect, useState, useRef } from "react";
import "../styles/AdminHome.css";
import { API, getImageUrl } from "../config/api";
import { showConfirm } from "../utils/customDialog";
const AdminHome = () => {
  const [heroBanners, setHeroBanners] = useState([]);
  const [newHeroImage, setNewHeroImage] = useState("");
  const [editingBanner, setEditingBanner] = useState(null);
  const [events, setEvents] = useState([]);
  const [newEvent, setNewEvent] = useState({ type: "text", content: "", video: "" });
  const [editingEvent, setEditingEvent] = useState(null);

  const [newBook, setNewBook] = useState({ title: "", author: "", price: "", image: "" });
  const [gallery, setGallery] = useState([]);
  const [newImage, setNewImage] = useState("");
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("hero");

  // ✅ popup ref (prevents stacking)
  const popupRef = useRef(null);

  // ================= POPUP FUNCTION =================
  const showPopup = (msg) => {
    if (popupRef.current) popupRef.current.remove();

    const div = document.createElement("div");
    popupRef.current = div;

    div.innerHTML = `
      <div style="
        position: fixed;
        top: 20px;
        right: 20px;
        background: #58181f;
        color: white;
        padding: 12px 15px;
        border-radius: 8px;
        z-index: 99999;
        box-shadow: 0 4px 12px rgba(0,0,0,0.3);
        font-size: 14px;
      ">
        ${msg}
      </div>
    `;

    document.body.appendChild(div);

    setTimeout(() => {
      div.remove();
      popupRef.current = null;
    }, 2500);
  };

  useEffect(() => {
    reloadData();
  }, []);

  const reloadData = () => {
    fetch(`${API}?action=getHome`)
      .then((res) => res.json())
      .then((data) => {
        const banners = data.heroBanners || (data.hero?.image ? [data.hero] : []);
        setHeroBanners(banners);
        setEvents(data.events || []);
        setGallery(data.gallery || []);
      })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  const handleFileUpload = async (e, type) => {
    const file = e.target.files[0];
    if (!file) return;

    const formData = new FormData();
    formData.append("file", file);

    try {
      const res = await fetch(`${API}?action=upload`, {
        method: "POST",
        body: formData,
      });

      const data = await res.json();

      if (data.status === "success") {
        if (type === "hero") {
          setNewHeroImage(data.url);
          showPopup("📸 Hero banner uploaded. Click 'Add to Hero Carousel' to save.");
        }
        if (type === "editHero") {
          setEditingBanner((prev) => ({ ...prev, image: data.url }));
          showPopup("📸 New slide image uploaded. Click 'Save Changes' to apply.");
        }
        if (type === "book") {
          setNewBook({ ...newBook, image: data.url });
          showPopup("📘 Book image uploaded");
        }
        if (type === "gallery") {
          setNewImage(data.url);
          showPopup("🖼 Image uploaded");
        }
      }
    } catch (err) {
      console.error(err);
    }
  };

  const addHeroBanner = async () => {
    if (!newHeroImage) return;
    try {
      const res = await fetch(`${API}?action=addHeroBanner`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ image: newHeroImage }),
      });
      const data = await res.json();
      if (data.status === "success" || data.status === "added") {
        setNewHeroImage("");
        reloadData();
        showPopup("✅ Hero banner added to carousel");
      } else {
        showPopup("❌ Failed to add hero banner");
      }
    } catch (err) {
      console.error(err);
      showPopup("❌ Error adding hero banner");
    }
  };

  const deleteHeroBanner = async (id) => {
    try {
      const res = await fetch(`${API}?action=deleteHeroBanner&id=${id}`);
      const data = await res.json();
      if (data.status === "deleted") {
        reloadData();
        showPopup("🗑️ Hero banner removed");
      } else {
        showPopup("❌ Failed to remove banner");
      }
    } catch (err) {
      console.error(err);
      showPopup("❌ Error removing banner");
    }
  };

  const updateHeroBanner = async () => {
    if (!editingBanner || !editingBanner.id || !editingBanner.image) return;
    try {
      const res = await fetch(`${API}?action=updateHeroBanner`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: editingBanner.id, image: editingBanner.image }),
      });
      const data = await res.json();
      if (data.status === "updated" || data.status === "success") {
        setEditingBanner(null);
        reloadData();
        showPopup("✅ Hero banner slide updated successfully");
      } else {
        showPopup("❌ Failed to update hero banner");
      }
    } catch (err) {
      console.error(err);
      showPopup("❌ Error updating hero banner");
    }
  };

  const getEmbedUrl = (url) => {
    if (!url) return "";
    try {
      const parsed = new URL(url);

      if (parsed.hostname.includes("youtube.com")) {
        const id = parsed.searchParams.get("v");
        return id ? `https://www.youtube.com/embed/${id}` : url;
      }

      if (parsed.hostname === "youtu.be") {
        const id = parsed.pathname.slice(1);
        return `https://www.youtube.com/embed/${id}`;
      }

      return url;
    } catch {
      return url;
    }
  };

  const handleVideoChange = (value) => {
    setNewEvent({ ...newEvent, video: getEmbedUrl(value) });
  };

  const addEvent = async () => {
    await fetch(`${API}?action=addEvent`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(newEvent),
    });

    setNewEvent({ type: "text", content: "", video: "" });
    reloadData();
    showPopup("🎉 Event added successfully");
  };

  const handleEditClick = (eventItem) => {
    setEditingEvent({ ...eventItem });
  };

  const handleEditVideoChange = (value) => {
    setEditingEvent((prev) => ({ ...prev, video: getEmbedUrl(value) }));
  };

  const updateEvent = async () => {
    if (!editingEvent || !editingEvent.id) return;
    try {
      const res = await fetch(`${API}?action=updateEvent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(editingEvent),
      });
      const data = await res.json();
      if (data.status === "updated") {
        setEditingEvent(null);
        reloadData();
        showPopup("✅ Announcement updated successfully");
      } else {
        showPopup("❌ Failed to update announcement");
      }
    } catch (err) {
      console.error(err);
      showPopup("❌ Error updating announcement");
    }
  };

  const deleteEvent = async (id) => {
    await fetch(`${API}?action=deleteEvent&id=${id}`);
    reloadData();
    showPopup("🗑 Event deleted");
  };

  const addImage = async () => {
    await fetch(`${API}?action=addGallery`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ image: newImage }),
    });

    setNewImage("");
    reloadData();
    showPopup("🖼 Image saved to gallery");
  };

  const deleteImage = async (id) => {
    await fetch(`${API}?action=deleteGallery&id=${id}`);
    reloadData();
    showPopup("🗑 Image deleted");
  };


  if (loading) {
    return (
      <div className="admin-loading">
        <span className="spinner-border text-maroon" role="status" style={{ marginRight: "10px" }}></span>
        Loading Home Control Data...
      </div>
    );
  }

  return (
    <div className="admin-container home-admin-container">
      
      {/* ================= HEADER ================= */}
      <div className="home-header">
        <h2>
          <i className="bi bi-house-gear-fill" style={{ color: "#5d1d2b", marginRight: "10px" }}></i> Home Page Customization
        </h2>
        <p>Configure hero banner images, live event announcements, and media gallery</p>
      </div>

      {/* ================= IN-PAGE NAVIGATION NAVBAR ================= */}
      <div className="in-page-navbar">
        <button
          type="button"
          className={`in-page-nav-link ${activeTab === "hero" ? "active" : ""}`}
          onClick={() => setActiveTab("hero")}
        >
          <i className="bi bi-image"></i> Hero Banner
        </button>
        <span className="in-page-navbar-divider">|</span>
        <button
          type="button"
          className={`in-page-nav-link ${activeTab === "events" ? "active" : ""}`}
          onClick={() => setActiveTab("events")}
        >
          <i className="bi bi-calendar-event"></i> Live Events
        </button>
        <span className="in-page-navbar-divider">|</span>
        <button
          type="button"
          className={`in-page-nav-link ${activeTab === "gallery" ? "active" : ""}`}
          onClick={() => setActiveTab("gallery")}
        >
          <i className="bi bi-images"></i> Photo Gallery
        </button>
      </div>

      {/* ================= STATS PANEL ================= */}
      <div className="admin-stats">
        <div className="stat-card" onClick={() => setActiveTab("hero")} style={{ cursor: "pointer" }}>
          <div className="stat-info">
            <h4>Hero Banners</h4>
            <p style={{ fontSize: "18px", fontWeight: "700", marginTop: "4px", color: heroBanners.length > 0 ? "#10b981" : "#ef4444" }}>
              {heroBanners.length > 0 ? `${heroBanners.length} Active` : "Not Set"}
            </p>
          </div>
          <div className="stat-icon-wrapper">
            <i className="bi bi-images"></i>
          </div>
        </div>

        <div className="stat-card" onClick={() => setActiveTab("events")} style={{ cursor: "pointer" }}>
          <div className="stat-info">
            <h4>Live Announcements</h4>
            <p>{events.length}</p>
          </div>
          <div className="stat-icon-wrapper">
            <i className="bi bi-megaphone-fill"></i>
          </div>
        </div>

        <div className="stat-card" onClick={() => setActiveTab("gallery")} style={{ cursor: "pointer" }}>
          <div className="stat-info">
            <h4>Gallery Photos</h4>
            <p>{gallery.length}</p>
          </div>
          <div className="stat-icon-wrapper">
            <i className="bi bi-images"></i>
          </div>
        </div>
      </div>

      {/* ================= HERO ================= */}
      {activeTab === "hero" && (
        <section className="admin-section" id="hero-section">
          <div className="admin-section-header">
            <h2>
              <i className="bi bi-images"></i> Hero Banner Carousel ({heroBanners.length} Slides)
            </h2>
          </div>
          
          <div className="admin-entry-card">
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "12px", flexWrap: "wrap" }}>
              <span style={{ background: "#fef3c7", color: "#92400e", padding: "4px 12px", borderRadius: "20px", fontSize: "12px", fontWeight: "700", display: "inline-flex", alignItems: "center", gap: "5px" }}>
                <i className="bi bi-clock-history"></i> 3-Second Auto-Scroll
              </span>
              <span style={{ fontSize: "13px", color: "#64748b" }}>
                Images rotate automatically on the homepage every 3 seconds with smooth transitions.
              </span>
            </div>

            <p style={{ margin: "0 0 20px 0", fontSize: "14px", color: "#64748b" }}>
              Upload high-quality panoramic images (recommended 1920x600px). Each image added here will automatically rotate on the homepage.
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "20px", marginBottom: "20px" }}>
              {/* Custom Upload Zone */}
              <div className="home-upload-zone">
                <i className="bi bi-cloud-upload" style={{ fontSize: "28px", color: "#94a3b8", marginBottom: "8px" }}></i>
                <p style={{ margin: 0, fontSize: "13px", fontWeight: "600", color: "#475569" }}>
                  {newHeroImage ? "Change Selected Image" : "Click to select panoramic banner image"}
                </p>
                <input 
                  type="file" 
                  accept="image/*"
                  id="hero-file-upload"
                  onChange={(e) => handleFileUpload(e, "hero")}
                />
              </div>

              {newHeroImage && (
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8fafc", padding: "14px", borderRadius: "12px", border: "1px solid #cbd5e1" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
                    <img src={getImageUrl(newHeroImage)} width="120" height="50" style={{ objectFit: "cover", borderRadius: "8px", border: "1px solid #cbd5e1" }} alt="preview" />
                    <div>
                      <span style={{ fontSize: "13px", fontWeight: "700", color: "#1e293b", display: "block" }}>Selected Banner Ready</span>
                      <span style={{ fontSize: "12px", color: "#64748b" }}>Click "Add to Hero Carousel" below to activate this slide</span>
                    </div>
                  </div>
                  <button
                    type="button"
                    className="btn-cancel"
                    onClick={() => setNewHeroImage("")}
                    style={{ padding: "6px 12px", fontSize: "12px" }}
                  >
                    Cancel
                  </button>
                </div>
              )}
            </div>

            <div style={{ display: "flex", gap: "12px", alignItems: "center", marginBottom: "20px" }}>
              <button 
                type="button" 
                className="btn-premium" 
                onClick={addHeroBanner}
                disabled={!newHeroImage}
              >
                <i className="bi bi-plus-circle-fill"></i> Add to Hero Carousel
              </button>
            </div>
          </div>

          <h3 style={{ margin: "25px 0 15px 0", fontSize: "18px", fontWeight: "700", color: "#5d1d2b", display: "flex", alignItems: "center", gap: "8px" }}>
            <i className="bi bi-collection-play"></i> Active Hero Banner Slides ({heroBanners.length})
          </h3>

          <div className="admin-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(280px, 1fr))", gap: "20px" }}>
            {heroBanners.length > 0 ? (
              heroBanners.map((banner, index) => (
                <div 
                  key={banner.id || index} 
                  className="admin-entry-card" 
                  style={{ 
                    padding: "16px", 
                    margin: 0, 
                    display: "flex",
                    flexDirection: "column",
                    gap: "12px",
                    position: "relative"
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ background: "#5d1d2b", color: "#fff", padding: "3px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: "700" }}>
                      Slide {index + 1}
                    </span>
                    <span style={{ fontSize: "11px", color: "#64748b" }}>
                      Auto-scroll: 3s
                    </span>
                  </div>

                  <div style={{ width: "100%", height: "130px", borderRadius: "8px", overflow: "hidden", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                    <img 
                      src={getImageUrl(banner.image)} 
                      alt={`Slide ${index + 1}`} 
                      style={{ width: "100%", height: "100%", objectFit: "cover" }} 
                    />
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px", marginTop: "2px" }}>
                    <button
                      type="button"
                      className="btn-premium"
                      onClick={() => setEditingBanner({ ...banner, index })}
                      style={{ height: "34px", padding: "0 10px", fontSize: "12px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "5px" }}
                    >
                      <i className="bi bi-pencil-square"></i> Edit
                    </button>
                    <button
                      type="button"
                      className="btn-premium-outline"
                      onClick={async () => {
                        const ok = await showConfirm({
                          title: "Remove Slide",
                          message: `Are you sure you want to remove Slide ${index + 1}?`,
                          subMessage: "This banner slide will no longer be visible on the homepage.",
                          confirmText: "Remove Slide",
                          type: "danger"
                        });
                        if (ok) {
                          deleteHeroBanner(banner.id);
                        }
                      }}
                      style={{ height: "34px", padding: "0 10px", fontSize: "12px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "5px", color: "#dc2626", borderColor: "#fca5a5" }}
                    >
                      <i className="bi bi-trash"></i> Delete
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="admin-empty-state" style={{ gridColumn: "1 / -1" }}>
                <i className="bi bi-images"></i>
                <h3>No Hero Banners Found</h3>
                <p>Upload panoramic images above to populate the 3-second auto-scrolling hero carousel.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ================= EVENTS ================= */}
      {activeTab === "events" && (
        <section className="admin-section" id="events-section">
          <div className="admin-section-header">
            <h2>
              <i className="bi bi-calendar2-event-fill"></i> Live Announcement Board
            </h2>
          </div>

          {/* CREATE EVENT BOX */}
          <div className="admin-entry-card" style={{ marginBottom: "30px" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "700", color: "#5d1d2b" }}>
              Create New Announcement
            </h3>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "16px", marginBottom: "20px" }}>
              <div className="form-group">
                <label>Announcement Type</label>
                <select
                  value={newEvent.type}
                  onChange={(e) => setNewEvent({ ...newEvent, type: e.target.value })}
                >
                  <option value="text">Announcement Text</option>
                  <option value="video">YouTube Video URL</option>
                </select>
              </div>

              <div className="form-group">
                <label>{newEvent.type === "text" ? "Announcement Text Content" : "YouTube Video URL"}</label>
                {newEvent.type === "text" ? (
                  <input
                    type="text"
                    placeholder="Enter event notification or announcement headline..."
                    value={newEvent.content}
                    onChange={(e) => setNewEvent({ ...newEvent, content: e.target.value })}
                  />
                ) : (
                  <input
                    type="text"
                    placeholder="Paste shareable YouTube Video URL (e.g. https://youtu.be/xxx)..."
                    value={newEvent.video}
                    onChange={(e) => handleVideoChange(e.target.value)}
                  />
                )}
              </div>
            </div>

            <button 
              type="button" 
              className="btn-premium" 
              onClick={addEvent}
            >
              <i className="bi bi-plus-circle"></i> Add Announcement
            </button>
          </div>

          {/* ================= EVENTS GRID ================= */}
          <h3 style={{ margin: "0 0 20px 0", fontSize: "16px", fontWeight: "700", color: "#5d1d2b" }}>
            Current Live Announcements ({events.length})
          </h3>
          <div className="admin-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(320px, 1fr))", gap: "24px" }}>
            {events.length > 0 ? (
              events.map((e) => (
                <div 
                  key={e.id} 
                  className="admin-entry-card" 
                  style={{ 
                    display: "flex", 
                    flexDirection: "column", 
                    justifyContent: "space-between", 
                    margin: 0,
                  }}
                >
                  <div style={{ flex: 1, marginBottom: "20px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "12px", borderBottom: "1px solid #f1f5f9", paddingBottom: "8px" }}>
                      <span style={{ fontSize: "11px", fontWeight: "700", textTransform: "uppercase", letterSpacing: "0.5px", color: e.type === "text" ? "#0f766e" : "#b25e00", background: e.type === "text" ? "#ccfbf1" : "#fef3c7", padding: "4px 8px", borderRadius: "6px" }}>
                        {e.type === "text" ? "Text Notice" : "Video Embed"}
                      </span>
                      <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
                        <button
                          type="button"
                          className="action-edit"
                          onClick={() => handleEditClick(e)}
                          title="Edit Announcement"
                        >
                          <i className="bi bi-pencil-square"></i>
                        </button>
                        <button
                          type="button"
                          className="action-delete"
                          onClick={async () => {
                            const ok = await showConfirm({
                              title: "Delete Announcement",
                              message: "Are you sure you want to delete this event?",
                              subMessage: "This announcement item will be permanently removed.",
                              confirmText: "Delete Event",
                              type: "danger"
                            });
                            if (ok) {
                              deleteEvent(e.id);
                            }
                          }}
                          title="Delete Announcement"
                        >
                          <i className="bi bi-trash"></i>
                        </button>
                      </div>
                    </div>

                    {e.type === "text" ? (
                      <div style={{ display: "flex", gap: "10px", alignItems: "flex-start" }}>
                        <i className="bi bi-megaphone-fill" style={{ color: "#5d1d2b", fontSize: "18px" }}></i>
                        <p style={{ margin: 0, fontSize: "14px", lineHeight: "1.5", color: "#1e293b", fontWeight: "500" }}>{e.content}</p>
                      </div>
                    ) : (
                      <div style={{ borderRadius: "10px", overflow: "hidden", border: "1px solid #cbd5e1" }}>
                        <iframe
                          src={getEmbedUrl(e.video)}
                          width="100%"
                          height="180"
                          title="event-video"
                          allowFullScreen
                          style={{ border: "none", display: "block" }}
                        />
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="admin-empty-state" style={{ gridColumn: "1 / -1" }}>
                <i className="bi bi-megaphone"></i>
                <h3>No Live Announcements</h3>
                <p>Post updates or video highlights using the form above.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ================= GALLERY ================= */}
      {activeTab === "gallery" && (
        <section className="admin-section" id="gallery-section">
          <div className="admin-section-header">
            <h2>
              <i className="bi bi-images"></i> Homepage Photo Gallery
            </h2>
          </div>

          <div className="admin-entry-card" style={{ marginBottom: "30px" }}>
            <h3 style={{ margin: "0 0 16px 0", fontSize: "16px", fontWeight: "700", color: "#5d1d2b" }}>
              Add New Photo to Gallery
            </h3>
            
            <p style={{ margin: "0 0 20px 0", fontSize: "14px", color: "#64748b" }}>
              Upload and showcase spiritual, cultural, and organizational photos in the scrolling slideshow gallery.
            </p>

            <div style={{ display: "grid", gridTemplateColumns: "1fr", gap: "20px", marginBottom: "20px" }}>
              {/* Custom Upload Zone */}
              <div className="home-upload-zone">
                <i className="bi bi-file-earmark-image" style={{ fontSize: "28px", color: "#94a3b8", marginBottom: "8px" }}></i>
                <p style={{ margin: 0, fontSize: "13px", fontWeight: "600", color: "#475569" }}>
                  Click to select gallery photo
                </p>
                <input 
                  type="file" 
                  accept="image/*"
                  id="gallery-file-upload"
                  onChange={(e) => handleFileUpload(e, "gallery")}
                />
              </div>

              {newImage && (
                <div style={{ display: "flex", alignItems: "center", gap: "12px", background: "#f8fafc", padding: "12px", borderRadius: "10px", border: "1px solid #cbd5e1" }}>
                  <img src={newImage} width="60" height="60" style={{ objectFit: "cover", borderRadius: "8px", border: "1px solid #cbd5e1" }} alt="preview" />
                  <div>
                    <span style={{ fontSize: "13px", fontWeight: "600", color: "#1e293b", display: "block" }}>Selected Image Preview</span>
                    <span style={{ fontSize: "12px", color: "#64748b" }}>Ready to upload to homepage slideshow</span>
                  </div>
                </div>
              )}
            </div>

            <button 
              type="button" 
              className="btn-premium" 
              onClick={addImage}
              disabled={!newImage}
            >
              <i className="bi bi-plus-lg"></i> Add Image to Gallery
            </button>
          </div>

          <h3 style={{ margin: "0 0 20px 0", fontSize: "16px", fontWeight: "700", color: "#5d1d2b" }}>
            Current Gallery Photos ({gallery.length})
          </h3>
          <div className="admin-grid" style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))", gap: "24px" }}>
            {gallery.length > 0 ? (
              gallery.map((img) => (
                <div 
                  key={img.id} 
                  className="admin-entry-card" 
                  style={{ 
                    padding: "16px", 
                    margin: 0, 
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    gap: "12px"
                  }}
                >
                  <div style={{ width: "100%", height: "150px", borderRadius: "8px", overflow: "hidden", border: "1px solid #e2e8f0", background: "#f8fafc" }}>
                    <img 
                      src={getImageUrl(img.image)} 
                      alt="gallery item" 
                      style={{ width: "100%", height: "100%", objectFit: "contain" }} 
                    />
                  </div>

                  <button
                    type="button"
                    className="btn-premium-outline"
                    onClick={async () => {
                      const ok = await showConfirm({
                        title: "Delete Photo",
                        message: "Are you sure you want to delete this image?",
                        subMessage: "This photo will be permanently deleted from the gallery.",
                        confirmText: "Delete Photo",
                        type: "danger"
                      });
                      if (ok) {
                        deleteImage(img.id);
                      }
                    }}
                    style={{ width: "100%", height: "32px", padding: 0, fontSize: "12px" }}
                  >
                    <i className="bi bi-trash" style={{ marginRight: "4px" }}></i> Delete Photo
                  </button>
                </div>
              ))
            ) : (
              <div className="admin-empty-state" style={{ gridColumn: "1 / -1" }}>
                <i className="bi bi-images"></i>
                <h3>Gallery is Empty</h3>
                <p>Upload spiritual photos above to display them to users.</p>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ================= EDIT ANNOUNCEMENT MODAL ================= */}
      {editingEvent && (
        <div className="home-modal-overlay" onClick={() => setEditingEvent(null)}>
          <div className="home-modal-card" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>
                <i className="bi bi-pencil-square"></i> Edit Live Announcement
              </h3>
              <button
                type="button"
                className="close-modal-btn"
                onClick={() => setEditingEvent(null)}
                title="Close"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="modal-body">
              <div className="form-group" style={{ marginBottom: "18px" }}>
                <label>Announcement Type</label>
                <select
                  value={editingEvent.type || "text"}
                  onChange={(e) => setEditingEvent({ ...editingEvent, type: e.target.value })}
                >
                  <option value="text">Announcement Text</option>
                  <option value="video">YouTube Video URL</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: "16px" }}>
                <label>{editingEvent.type === "text" ? "Announcement Text Content" : "YouTube Video URL"}</label>
                {editingEvent.type === "text" ? (
                  <textarea
                    rows={4}
                    placeholder="Enter event notification or announcement headline..."
                    value={editingEvent.content || ""}
                    onChange={(e) => setEditingEvent({ ...editingEvent, content: e.target.value })}
                    style={{ minHeight: "100px", resize: "vertical" }}
                  />
                ) : (
                  <div>
                    <input
                      type="text"
                      placeholder="Paste shareable YouTube Video URL (e.g. https://youtu.be/xxx)..."
                      value={editingEvent.video || ""}
                      onChange={(e) => handleEditVideoChange(e.target.value)}
                    />
                    {editingEvent.video && (
                      <div style={{ borderRadius: "10px", overflow: "hidden", border: "1px solid #cbd5e1", marginTop: "12px" }}>
                        <iframe
                          src={getEmbedUrl(editingEvent.video)}
                          width="100%"
                          height="180"
                          title="edit-preview-video"
                          allowFullScreen
                          style={{ border: "none", display: "block" }}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setEditingEvent(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-premium"
                onClick={updateEvent}
              >
                <i className="bi bi-check-circle"></i> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ================= EDIT HERO BANNER MODAL ================= */}
      {editingBanner && (
        <div className="home-modal-overlay" onClick={() => setEditingBanner(null)}>
          <div className="home-modal-card" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "620px" }}>
            <div className="modal-header">
              <h3>
                <i className="bi bi-pencil-square"></i> Edit Hero Banner (Slide {editingBanner.index + 1})
              </h3>
              <button
                type="button"
                className="close-modal-btn"
                onClick={() => setEditingBanner(null)}
                title="Close"
              >
                <i className="bi bi-x-lg"></i>
              </button>
            </div>

            <div className="modal-body">
              <p style={{ margin: "0 0 16px 0", fontSize: "13px", color: "#64748b" }}>
                Replace the banner image for <strong>Slide {editingBanner.index + 1}</strong>. The new image will automatically update on the homepage auto-scroll carousel.
              </p>

              {/* Current / New Image Preview */}
              <div style={{ marginBottom: "18px" }}>
                <label style={{ display: "block", marginBottom: "8px", fontWeight: "600", fontSize: "13px", color: "#334155" }}>
                  Slide Image Preview
                </label>
                <div style={{ width: "100%", height: "180px", borderRadius: "12px", overflow: "hidden", border: "1.5px solid #cbd5e1", background: "#f8fafc", position: "relative" }}>
                  <img
                    src={getImageUrl(editingBanner.image)}
                    alt="Slide preview"
                    style={{ width: "100%", height: "100%", objectFit: "cover" }}
                  />
                </div>
              </div>

              {/* Upload Zone to Replace */}
              <div className="home-upload-zone" style={{ padding: "20px 16px" }}>
                <i className="bi bi-cloud-upload" style={{ fontSize: "28px", color: "#94a3b8", marginBottom: "6px" }}></i>
                <p style={{ margin: 0, fontSize: "13px", fontWeight: "600", color: "#475569" }}>
                  Click to select new image to replace Slide {editingBanner.index + 1}
                </p>
                <span style={{ fontSize: "11.5px", color: "#94a3b8", display: "block", marginTop: "4px" }}>
                  Supports PNG, JPG, JPEG, WEBP (Recommended: 1920x600)
                </span>
                <input
                  type="file"
                  accept="image/*"
                  id="edit-hero-file-upload"
                  onChange={(e) => handleFileUpload(e, "editHero")}
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn-cancel"
                onClick={() => setEditingBanner(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="btn-premium"
                onClick={updateHeroBanner}
              >
                <i className="bi bi-check-circle"></i> Save Changes
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default AdminHome;
